/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Swal from "sweetalert2";
import Moneda from "../../../components/Presupuesto/Moneda";
import {
  AportacionPartidaAdd,
  AportacionPartidaDelete,
  AportacionesPartidaGet,
} from "../../../service/partidaDetalle";
import "./ModalDetalle.css";

/**
 * Aportaciones de una partida.
 *
 * Se ve quién pone qué y cuánto falta para cubrir la partida. Antes sólo se
 * capturaba un monto suelto, sin decir de qué aportante venía, así que en la
 * portada del presupuesto no aparecía por ningún lado.
 */
export default function ModalAportacion({
  ModalAportacion: abierto,
  toggleAportacion,
  IdAportacion,
  token,
  PresupuestosGetService,
  id,
}) {
  const [aportaciones, setAportaciones] = useState([]);
  const [aportantes, setAportantes] = useState([]);
  const [partida, setPartida] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aportante, setAportante] = useState("");
  const [monto, setMonto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    if (!IdAportacion) return;

    setCargando(true);
    AportacionesPartidaGet(IdAportacion, token)
      .then((respuesta) => {
        setAportaciones(respuesta?.aportaciones ?? []);
        setAportantes(respuesta?.aportantes ?? []);
        setPartida(respuesta?.partida ?? null);
      })
      .catch(() => setAportaciones([]))
      .finally(() => setCargando(false));
  }, [IdAportacion, token]);

  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  const aportado = aportaciones.reduce(
    (suma, a) => suma + (Number(a.monto) || 0),
    0
  );
  const falta = (partida?.total ?? 0) - aportado;

  const agregar = async (evento) => {
    evento.preventDefault();

    setGuardando(true);
    try {
      const respuesta = await AportacionPartidaAdd(
        {
          presupuestos_hijos_id: IdAportacion,
          aportantes_id: aportante,
          monto,
        },
        token
      );

      if (!respuesta?.guardado) {
        throw new Error(respuesta?.message || "No se pudo guardar la aportación");
      }

      setMonto("");
      cargar();
      if (PresupuestosGetService) PresupuestosGetService(id, token);
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: error.message });
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (idAportacion) => {
    const confirmacion = await Swal.fire({
      icon: "warning",
      title: "¿Quitar esta aportación?",
      showCancelButton: true,
      confirmButtonText: "Sí, quitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#a81818",
    });

    if (!confirmacion.isConfirmed) return;

    await AportacionPartidaDelete(idAportacion, token);
    cargar();
  };

  return (
    <Modal
      isOpen={abierto}
      toggle={toggleAportacion}
      size="lg"
      className="modal-lci"
      contentClassName="modal-lci-contenido"
    >
      <ModalHeader toggle={toggleAportacion}>
        <span className="modal-lci-titulo">Aportaciones</span>
        <span className="modal-lci-sub">
          {partida?.nombre || "Partida"} · <Moneda valor={partida?.total} />
        </span>
      </ModalHeader>
      <ModalBody>
        <div className="detalle-resumen">
          <div>
            <span className="detalle-resumen-etiqueta">Aportado</span>
            <span className="detalle-resumen-valor">
              <Moneda valor={aportado} />
            </span>
          </div>
          <div>
            <span className="detalle-resumen-etiqueta">
              {falta >= 0 ? "Falta" : "Sobra"}
            </span>
            <span
              className={`detalle-resumen-valor ${
                Math.abs(falta) < 0.01 ? "es-cuadrado" : "es-pendiente"
              }`}
            >
              <Moneda valor={Math.abs(falta)} />
            </span>
          </div>
        </div>

        {aportantes.length === 0 ? (
          <p className="detalle-aviso">
            Este proyecto todavía no tiene aportantes. Se dan de alta al importar
            el presupuesto desde el Excel.
          </p>
        ) : (
          <form className="detalle-form" onSubmit={agregar}>
            <div className="detalle-campo es-ancho">
              <label htmlFor="aportacion-quien">Aportante</label>
              <select
                id="aportacion-quien"
                value={aportante}
                onChange={(e) => setAportante(e.target.value)}
                required
              >
                <option value="">Elige quién aporta</option>
                {aportantes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="detalle-campo">
              <label htmlFor="aportacion-monto">Monto</label>
              <input
                id="aportacion-monto"
                type="number"
                step="any"
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <button type="submit" className="crud-add-btn" disabled={guardando}>
              {guardando ? (
                <span className="spinner-boton" aria-hidden="true" />
              ) : (
                <i className="bi bi-plus-lg" aria-hidden="true"></i>
              )}
              Guardar aportación
            </button>
          </form>
        )}

        <div className="dtable-scroll">
          <table className="dtable">
            <thead>
              <tr>
                <th>Aportante</th>
                <th>Tipo</th>
                <th className="text-end">Monto</th>
                <th className="text-center">Quitar</th>
              </tr>
            </thead>
            <tbody>
              {cargando && (
                <tr>
                  <td colSpan={4} className="dtable-empty">
                    Cargando las aportaciones...
                  </td>
                </tr>
              )}

              {!cargando && aportaciones.length === 0 && (
                <tr>
                  <td colSpan={4} className="dtable-empty">
                    Esta partida todavía no tiene aportaciones.
                  </td>
                </tr>
              )}

              {!cargando &&
                aportaciones.map((a) => (
                  <tr key={a.id}>
                    <td>{a.aportante}</td>
                    <td>{a.tipo}</td>
                    <td className="text-end">
                      <Moneda valor={a.monto} />
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        title="Quitar la aportación"
                        onClick={() => borrar(a.id)}
                      >
                        <i className="mdi mdi-delete-forever"></i>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </ModalBody>
    </Modal>
  );
}
